import { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import AppScriptAPI from '../services/appscriptAPI';
import { useNavigate } from 'react-router-dom';
import { useUserPermissions } from '../contexts/UserPermissionsContext';
import AppToast from '../components/AppToast';
import { filterLocationData } from '../utils/accessControl';

function Register() {
  const navigate = useNavigate();
  const { userPermissions } = useUserPermissions();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [locationData, setLocationData] = useState({
    provinces: [],
    districts: {},
    divisions: {},
  });
  const [sheetId, setSheetId] = useState('');
  const [uniqueId, setUniqueId] = useState('');

  const [formData, setFormData] = useState({
    provinceCode: '',
    districtCode: '',
    dsdCode: '',
    name: '',
    nic: '',
  });

  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // Load location data on component mount
  useEffect(() => {
    loadLocationData();
  }, []);

  const loadLocationData = async () => {
    try {
      const data = await AppScriptAPI.getLocationData();
      setLocationData(filterLocationData(data, userPermissions));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'Failed to load location data: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleProvinceChange = (e) => {
    const provinceCode = e.target.value;
    setFormData({
      ...formData,
      provinceCode: provinceCode,
      districtCode: '',
      dsdCode: '',
    });
  };

  const handleDistrictChange = (e) => {
    const districtCode = e.target.value;
    setFormData({
      ...formData,
      districtCode: districtCode,
      dsdCode: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateNIC = (nic) => {
    if (!nic) return true; // NIC is optional
    const nicPattern = /^([0-9]{9}[VX]|[0-9]{12})$/;
    return nicPattern.test(nic.toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate NIC if provided
    const nicValue = formData.nic.trim().toUpperCase();
    if (nicValue && !validateNIC(nicValue)) {
      setAlert({
        show: true,
        message:
          'Invalid NIC format. Please enter a valid NIC number (9 digits + V/X or 12 digits).',
        type: 'danger',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);

    try {
      const response = await AppScriptAPI.submitRegistration({
        ...formData,
        nic: nicValue,
      });

      if(response.sheetId && response.uniqueId){
        setSheetId(response.sheetId);
        setUniqueId(response.uniqueId);
      }

      // Parse the success message to extract details
      if (response.success && response.message) {
        const message = response.message;

        // Extract details from message
        const nameMatch = message.match(/Name:\s*(.+?)(?:\n|ID:)/);
        const idMatch = message.match(/ID:\s*(\d+)/);
        const nicMatch = message.match(/NIC:\s*(.+?)(?:\n|Location:)/);
        const locationMatch = message.match(/Location:\s*(.+?)$/);

        const extractedData = {
          name: nameMatch ? nameMatch[1].trim() : formData.name,
          id: idMatch ? idMatch[1].trim() : 'N/A',
          nic: nicMatch ? nicMatch[1].trim() : nicValue || 'Not provided',
          location: locationMatch ? locationMatch[1].trim() : 'N/A',
        };

        setAlert({
          show: true,
          type: 'success',
          details: extractedData,
        });

        // Reset form
        setFormData({
          provinceCode: '',
          districtCode: '',
          dsdCode: '',
          name: '',
          nic: '',
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = () => {
    if (uniqueId && sheetId) {
      navigate(
        `/update/basic/${uniqueId}?sheetId=${sheetId}`
      );
    } else {
      setAlert({
        show: true,
        message: 'Unable to update: Missing required record data.',
        type: 'danger',
      });
    }
  };

  const handleNewRegistration = () => {
    // Reset all form data
    setFormData({
      provinceCode: '',
      districtCode: '',
      dsdCode: '',
      name: '',
      nic: '',
    });
    
    // Clear alert
    setAlert({ show: false, message: '', type: '' });
    
    // Clear stored IDs
    setSheetId('');
    setUniqueId('');
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="container mt-5" style={{ minHeight: '100vh' }}>
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading form data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5" style={{ minHeight: '100vh' }}>
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <Card className="form-card">
            <Card.Body className="p-4">
              <h2 className="card-title mb-4">Registration Form</h2>

              {alert.show &&
                (alert.details ? (
                  // Success message with formatted details
                  <Alert
                    variant="success"
                    dismissible
                    onClose={() => setAlert({ ...alert, show: false })}
                  >
                    <Alert.Heading className="d-flex align-items-center">
                      Registration Successful!
                    </Alert.Heading>
                    <hr />
                    <div className="registration-success-details">
                      <Row className="mb-2">
                        <Col xs={4} className="fw-bold text-end">
                          Name:
                        </Col>
                        <Col xs={8}>{alert.details.name}</Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs={4} className="fw-bold text-end">
                          DoSS ID:
                        </Col>
                        <Col xs={8}>
                          <span
                            className="badge bg-primary"
                            style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}
                          >
                            {alert.details.id}
                          </span>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs={4} className="fw-bold text-end">
                          NIC:
                        </Col>
                        <Col xs={8}>{alert.details.nic}</Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs={4} className="fw-bold text-end">
                          Location:
                        </Col>
                        <Col xs={8}>{alert.details.location}</Col>
                      </Row>
                    </div>
                    <hr />
                    <p className="mb-0 text-muted small">
                      <i className="bi bi-info-circle me-1"></i>
                      Please save the DoSS ID for future reference. You can now update additional details.
                    </p>
                    <div className="d-grid gap-2 mt-3">
                      <Button variant="success" onClick={handleUpdate}>
                        <i className="bi bi-pencil-square me-2"></i>
                        Update Details
                      </Button>
                      <Button variant="secondary" onClick={handleNewRegistration}>
                        <i className="bi bi-plus-circle me-2"></i>
                        New Registration
                      </Button>
                    </div>
                  </Alert>
                ) : null)}
              <AppToast
                show={alert.show && !alert.details}
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(a => ({ ...a, show: false }))}
              />

              {/* Only show form if no success details are present */}
              {!alert.details && (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Province</Form.Label>
                    <Form.Select
                      name="provinceCode"
                      value={formData.provinceCode}
                      onChange={handleProvinceChange}
                      required
                    >
                      <option value="">Select Province</option>
                      {locationData.provinces.map((province) => (
                        <option key={province.code} value={province.code}>
                          {province.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>District</Form.Label>
                    <Form.Select
                      name="districtCode"
                      value={formData.districtCode}
                      onChange={handleDistrictChange}
                      disabled={!formData.provinceCode}
                      required
                    >
                      <option value="">Select District</option>
                      {formData.provinceCode &&
                        locationData.districts[formData.provinceCode]?.map(
                          (district) => (
                            <option key={district.code} value={district.code}>
                              {district.name}
                            </option>
                          )
                        )}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>DS Division</Form.Label>
                    <Form.Select
                      name="dsdCode"
                      value={formData.dsdCode}
                      onChange={handleInputChange}
                      disabled={!formData.districtCode}
                      required
                    >
                      <option value="">Select DS Division</option>
                      {formData.districtCode &&
                        locationData.divisions[formData.districtCode]?.map(
                          (division) => (
                            <option key={division.code} value={division.code}>
                              {division.name}
                            </option>
                          )
                        )}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Name With Initials</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Ex. Smith J.A.S"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>NIC Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="nic"
                      value={formData.nic}
                      onChange={handleInputChange}
                      placeholder="Ex. 199512345678 or 951234567V"
                    />
                    <Form.Text className="text-muted">
                      Please enter it if the person has a NIC (Old: 9 digits +
                      V/X, New: 12 digits)
                    </Form.Text>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Register;